import React, { FC, useState } from 'react';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import { useDataProvider } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import WalletAssignedAwardList from './components/AssignedAwardList';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { WalletAssignedAwardListParam, WalletAssignedAwardListRes } from '../../../types/assigned-award-type';
import { WALLET_AWARD_PATH, WALLET_BASE_PATH } from '../../../constants';
import { useHistory } from 'react-router-dom';
import { getWalletAssignedAwardList } from '../../../network/awardCrud';

type AwardListingPageProps = {} & ParamsProps;

const initialState: WalletAssignedAwardListParam = {
  pageSize: 5,
  page: 1,
  awardId: '',
  agentCode: '',
  promoCode: '',
  markedAsUsed: '',
};

const paramsInitiator = (initialParams?: Record<string, string>): WalletAssignedAwardListParam => {
  return initialParams
    ? {
        page: 1,
        pageSize: 5,
        awardId: initialParams.awardId ?? undefined,
        agentCode: initialParams.agentCode ?? undefined,
        promoCode: initialParams.promoCode ?? undefined,
        markedAsUsed: initialParams.markedAsUsed ?? undefined,
      }
    : initialState;
};

export const WalletAssignedAwardFilterKeys = ['awardId'];

const AssignedAwardListingPage: FC<AwardListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [filterState, setFilterState] = useState<WalletAssignedAwardListParam>(paramsInitiator(initialParams));

  const [listRes, setListRes] = useState<WalletAssignedAwardListRes>();
  const { isLoading, refreshData } = useDataProvider<WalletAssignedAwardListRes>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await getWalletAssignedAwardList(filterState, dispatch);
      } catch (err) {
        console.error(err);
      }
    },
    setListRes,
    false,
  );

  return (
    <>
      <PruFilter
        title={Translation('walletAssignedAward.filter')}
        enableBack={!!initialParams?.awardId}
        backBtnText={Translation('walletAssignedAward.backBtnText')}
        onBack={() => {
          history.push(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}`);
        }}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'awardId',
            initialValue: filterState.awardId,
            displayName: Translation('walletAssignedAward.common.awardId'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('walletAssignedAward.common.agentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'promoCode',
            initialValue: filterState.promoCode,
            displayName: Translation('walletAssignedAward.common.promoCode'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'markedAsUsed',
            displayName: Translation('walletAssignedAward.common.markedAsUsed'),
            initialValue: filterState.markedAsUsed,
            choices: [
              { displayName: Translation('app.select.all'), value: '' },
              { displayName: Translation(`walletAssignedAward.common.option.Y`), value: 'Y' },
              { displayName: Translation(`walletAssignedAward.common.option.N`), value: 'N' },
            ],
          },

          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'redemptionStartDate',
            fieldTo: 'redemptionEndDate',
            initialDateFrom: filterState.redemptionStartDate,
            initialDateTo: filterState.redemptionEndDate,
            displayName: Translation('walletAssignedAward.common.allowRedemptionDate'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'validityStartDate',
            fieldTo: 'validityEndDate',
            initialDateFrom: filterState.validityStartDate,
            initialDateTo: filterState.validityEndDate,
            displayName: Translation('walletAssignedAward.common.validityDate'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            awardId: data.awardId,
            agentCode: data.agentCode,
            promoCode: data.promoCode,
            markedAsUsed: data.markedAsUsed,
            redemptionStartDate: data.redemptionStartDate,
            redemptionEndDate: data.redemptionEndDate,
            validityStartDate: data.validityStartDate,
            validityEndDate: data.validityEndDate,
          });
        }}
        fetchData={refreshData}
      />
      <WalletAssignedAwardList
        isLoading={isLoading}
        listRes={listRes}
        onRefresh={refreshData}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            pageSize: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default AssignedAwardListingPage;

const filterWidth = { width: 200 };
